import type { AdminDiaryFilters } from "@/features/admin/diaries/api/adminDiariesApi";
import type { AdminDiariesFilterValues } from "@/features/admin/diaries/components/AdminDiariesFilterMenu";
import { emptyAdminDiariesFilterValues } from "@/features/admin/diaries/components/AdminDiariesFilterMenu";
import { AdminDiariesTable } from "@/features/admin/diaries/components/AdminDiariesTable";
import { AdminDiariesToolbar } from "@/features/admin/diaries/components/AdminDiariesToolbar";
import { isAuditorView } from "@/features/audit/api/auditorApi";
import { fetchDiariesAt } from "@/features/audit/api/fetchDiariesAt";
import { AuditorDiarySearch } from "@/features/audit/components/AuditorDiarySearch";
import { useAuditDiaryList } from "@/features/audit/hooks/useAuditDiaryList";
import { auditLogSearchSchema, serializeAuditLogSearch } from "@/features/audit/utils/auditFilterSearch";
import { auditLogRouteApi } from "@/features/audit/utils/auditLogRouteApi";
import { AuditSnapshotAsOfField } from "@/features/audit/components/AuditSnapshotAsOfField";
import { useAuth } from "@/utils/auth";
import { ui } from "@patient-watch/ui";
import { Button } from "@patient-watch/ui/button";
import { createFileRoute } from "@tanstack/react-router";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { Download } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "@patient-watch/ui/sonner";
import { useToastCopy } from "@/features/i18n/toastCopy";

dayjs.extend(utc);

export const Route = createFileRoute("/_app/audit/snapshot")({
    component: AuditLogSnapshotPage,
});

function AuditLogSnapshotPage() {
    const toastCopy = useToastCopy();
    const auth = useAuth();
    const auditorView = isAuditorView();
    const search = auditLogRouteApi.useSearch();
    const navigate = auditLogRouteApi.useNavigate();

    const [searchText, setSearchText] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [filterValues, setFilterValues] = useState<AdminDiariesFilterValues>(emptyAdminDiariesFilterValues);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [isExporting, setIsExporting] = useState(false);

    useEffect(() => {
        const handle = setTimeout(() => setDebouncedSearch(searchText.trim()), 300);
        return () => clearTimeout(handle);
    }, [searchText]);

    const filters: AdminDiaryFilters = useMemo(
        () => ({
            q: debouncedSearch || undefined,
            labels: filterValues.labels.length ? filterValues.labels : undefined,
            clinicians: filterValues.clinicians.length ? filterValues.clinicians : undefined,
            organisations: filterValues.organisations.length ? filterValues.organisations : undefined,
            page: 1,
            pageSize: 200,
        }),
        [debouncedSearch, filterValues]
    );

    const { data, isLoading, isFetching, error } = useAuditDiaryList(filters, searchText);
    const diaries = data?.diaries ?? [];

    // No selection means "export everything currently listed".
    const selectedExportIds = diaries.filter((d) => selectedIds.has(d.diary_id)).map((d) => d.diary_id);
    const exportDiaryIds = selectedExportIds.length > 0 ? selectedExportIds : diaries.map((d) => d.diary_id);

    const setAsOf = useCallback(
        (utcIso: string | undefined) => {
            void navigate({
                to: "/audit/snapshot",
                search: () =>
                    serializeAuditLogSearch({
                        ...auditLogSearchSchema.parse(search),
                        asOf: utcIso,
                    }),
                replace: true,
            });
        },
        [navigate, search]
    );

    const exportSnapshot = async () => {
        if (!auth.id) {
            toast.error(toastCopy.mustSignInToExportSnapshot);
            return;
        }
        if (exportDiaryIds.length === 0) {
            toast.error(toastCopy.noDiariesToExport);
            return;
        }
        if (!search.asOf) {
            toast.error(toastCopy.chooseSnapshotDateTime);
            return;
        }

        setIsExporting(true);
        try {
            const snapshot = await fetchDiariesAt(exportDiaryIds, auth.id, search.asOf);
            const blob = new Blob([JSON.stringify(snapshot, null, 2)], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `clinical-snapshot-${exportDiaryIds.length}-diaries-${dayjs.utc(search.asOf).format("YYYY-MM-DD-HHmm")}.json`;
            link.click();
            URL.revokeObjectURL(url);
            toast.success(
                toastCopy.snapshotExported(exportDiaryIds.length, dayjs.utc(search.asOf).format("YYYY-MM-DD HH:mm"))
            );
        } catch (exportError) {
            toast.error(exportError instanceof Error ? exportError.message : toastCopy.snapshotExportFailed);
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <div className="space-y-3">
            <p className="text-muted-foreground text-xs">
                Reconstruct what clinical data looked like at a cut-off date (e.g. for a PMCF report). Pick the diaries
                below, choose the as-of time, then download. This replays audited tables from the audit log - form
                definitions and patient profile are not included.
                {auditorView ? " Patient details in the download are hidden the same way." : ""}
            </p>

            <div className="flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[260px] max-w-md">
                    <AuditSnapshotAsOfField value={search.asOf} onChange={setAsOf} disabled={isExporting} />
                </div>
                <Button
                    variant="secondary"
                    size="sm"
                    className="shrink-0"
                    onClick={() => void exportSnapshot()}
                    disabled={isExporting || exportDiaryIds.length === 0 || !search.asOf}
                >
                    <Download className="mr-1.5 w-4 h-4" />
                    {isExporting
                        ? "Exporting..."
                        : `Download snapshot (${exportDiaryIds.length} ${exportDiaryIds.length === 1 ? "diary" : "diaries"})`}
                </Button>
            </div>

            {auditorView ? (
                <AuditorDiarySearch value={searchText} onChange={setSearchText} />
            ) : (
                <AdminDiariesToolbar
                    searchText={searchText}
                    onSearchTextChange={setSearchText}
                    filterValues={filterValues}
                    onApplyFilters={(values) => {
                        setSelectedIds(new Set());
                        setFilterValues(values);
                    }}
                    isApplying={isFetching}
                />
            )}

            {error && (
                <div className="bg-red-50 p-4 border border-red-200 rounded-md text-red-800 text-sm">
                    Error loading diaries: {error instanceof Error ? error.message : "Unknown error"}
                </div>
            )}

            {isLoading ? (
                <ui.Skeleton className="w-full h-64" />
            ) : (
                !error && (
                    <>
                        <AdminDiariesTable
                            diaries={diaries}
                            selectedIds={selectedIds}
                            onSelectedIdsChange={setSelectedIds}
                        />
                        <p className="text-muted-foreground text-xs">
                            Showing {diaries.length} of {data?.totalCount ?? 0} diaries
                            {(data?.totalCount ?? 0) > diaries.length ? " - refine the filters to narrow the list" : ""}
                            .{" "}
                            {selectedIds.size > 0
                                ? `${selectedIds.size} selected for export.`
                                : "Select rows to export a subset, or leave all unticked to export every diary listed."}
                        </p>
                    </>
                )
            )}
        </div>
    );
}
